import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import LocationPicker from '../components/common/LocationPicker'; 
import { useAuth } from '../context/AuthContext';
import { MagnifyingGlassIcon, MapPinIcon } from '@heroicons/react/24/solid';
import { useParams } from 'react-router-dom';
import i18n from '../i18n';
import { persistClientLang } from '../utils/lang';
import { useTranslation} from 'react-i18next';

const ProfilePage = () => {
  const { id } = useParams();
  const { user, setUser } = useAuth(); // Access global state
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const { t } = useTranslation();
  const roleKey = user?.role?.toLowerCase().trim().replace(/\s+/g, '-');
  
  // Search state for address
  const [addressQuery, setAddressQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    defaultLocation: { 
        lat: 48.8566, 
        lng: 2.3522,
        address: '' // Added address field
    },
    language: 'en' 
  });

  // 1. Fetch User Data on Load
  useEffect(() => {
    
    const fetchProfile = async () => {
      try {
        const res = id ? await api.get(`/auth/users/${id}`) : await api.get('/auth/me');
        const data = res?.data || null;

        if (!data) {
          console.error('No user data returned', res);
          setFormData({
            name: '',
            email: '',
            defaultLocation: { lat: 48.8566, lng: 2.3522, address: '' },
            language: ''
          });
          setLoading(false);
          return;
        }
        
          setFormData({
              name: res.data.name || '',
              email: res.data.email || '',
              defaultLocation: res.data.defaultLocation || { lat: 48.8566, lng: 2.3522, address: '' },
              language: res.data.language || 'en'
          });
          
        // Pre-fill the search box if an address exists
        if(res.data.defaultLocation?.address) {
            setAddressQuery(res.data.defaultLocation.address);
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error loading profile", err);
        setLoading(false);
      }
    };
    fetchProfile();
  }, [id]);

  // Helper: Reverse Geocode (Get Address from Coords)
  const fetchAddressFromCoords = async (lat, lng) => {
    try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
        const data = await res.json();
        if (data && data.display_name) {
            setAddressQuery(data.display_name); // Update UI text input
            return data.display_name;
        }
    } catch (err) {
        console.error("Reverse geocode failed", err);
    }
    return '';
  };

  // Helper: Forward Geocode (Get Coords from Text)
  const handleAddressSearch = async (e) => {
    e.preventDefault();
    if (!addressQuery) return;
    setIsSearching(true);

    try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressQuery)}`);
        const data = await res.json();
        if (data && data.length > 0) {
            const lat = parseFloat(data[0].lat);
            const lng = parseFloat(data[0].lon);
            const address = data[0].display_name;

            // Update state
            setFormData(prev => ({
                ...prev,
                defaultLocation: { lat, lng, address }
            }));
            setAddressQuery(address); // Standardize the text
        } else {
            alert(t('profile.alerts.address-not-found'));
        }
    } catch (err) {
        console.error(err);
        alert(t(`profile.alerts.address-search-error`) );
    } finally {
        setIsSearching(false);
    }
  };

  // 2. Handle save
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      // Send to Backend
      const res = await api.put('/auth/update-details', formData);
      
      // Update Global Context (so Map/Header updates immediately)
      // Assuming res.data returns the updated user object
      if (setUser) setUser(res.data);


      const lang = formData.language;
      persistClientLang(lang);        // keep key consistent with your utils  
      i18n.changeLanguage(lang);     // immediately switch translations

      setMessage({ type: 'success', text:  t(`profile.alerts.profile-updated-success`) });
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);


if (setUser) setUser(res.data); // update global user
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: t(`profile.alerts.profile-updated-error`)  });
    }
  };

  const handleMapLocationSelect = async (lat, lng) => {
      // 1. Immediately update coords in state
      const address = await fetchAddressFromCoords(lat, lng);
      
      setFormData(prev => ({
          ...prev, 
          defaultLocation: { lat, lng, address }
      }));
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Loading profile...</div>;

  return (
    <div className="max-w-4xl mx-auto py-10 px-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">  {t(`profile.title`)}</h1>
        {user?.role && (
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold uppercase">
                 {t(`profile.${roleKey}`, { defaultValue: user.role })}
            </span>
        )}
      </div>
      
      <div className="bg-white shadow-lg rounded-xl overflow-hidden">
        {/* Status Message */}
        {message.text && (
            <div className={`p-4 text-center text-sm font-bold ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {message.text}
            </div>
        )}

        <form onSubmit={handleSubmit} className="p-8">
          
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">{t('profile.full-name')}</label>
                <input 
                    type="text" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    placeholder={t('profile.full-name-placeholder')}
                />
            </div>
            <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">{t('profile.email')}</label>
                <input 
                    type="email" 
                    className="w-full px-4 py-2 border border-gray-200 bg-gray-50 text-gray-500 rounded-lg cursor-not-allowed"
                    value={formData.email}
                    disabled
                />
                <p className="text-xs text-gray-400 mt-1">{t('profile.email-not-changed')}</p>
            </div>
          </div>

          <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">{t('profile.language')}</label>
              <select 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                  value={formData.language}
                  onChange={e => setFormData({...formData, language: e.target.value})}
              >
                  <option value="en">English</option>
                  <option value="de">Deutsch</option>
                  <option value="es">Español</option>
                  <option value="fr">Français</option>
              </select>
          </div>

          {/* Map Preference Section */}
          <div className="border-t border-gray-100 pt-8">
            <div className="flex items-center gap-2 mb-4">
                <MapPinIcon className="h-6 w-6 text-[#1d5cab]" />
                <h2 className="text-xl font-bold text-gray-800">{t('profile.default-location')}</h2>
            </div>
            
            <p className="text-sm text-gray-500 mb-4">
                {t('profile.location-help')}
            </p>

            {/* Address Search Bar */}
            <div className="flex gap-2 mb-4">
                <div className="relative flex-grow">
                    <input 
                        type="text" 
                        placeholder={t('profile.location-placeholder')}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        value={addressQuery}
                        onChange={(e) => setAddressQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddressSearch(e)}
                    />
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
                <button 
                    type="button"
                    onClick={handleAddressSearch}
                    disabled={isSearching}
                    className="bg-[#1d5cab] hover:bg-[#2372bd] text-white px-4 py-2 rounded-lg text-sm font-bold disabled:opacity-50"
                >
                    {isSearching ? '...' : t('general.find')}
                </button>
            </div>
            
            {/* Map Picker */}
            <div className="h-72 w-full rounded-xl overflow-hidden border border-gray-300 relative z-0 shadow-inner">
                <LocationPicker 
                    lat={formData.defaultLocation.lat}
                    lng={formData.defaultLocation.lng}
                    onLocationSelect={handleMapLocationSelect} 
                />
            </div>
            
            {/* Coords Display */}
            <div className="flex justify-between items-center mt-2 text-xs text-gray-400">
                <span>{t('general.selected')}: {formData.defaultLocation.address || t('profile.custom-location')}</span>
                <span>{formData.defaultLocation.lat.toFixed(4)}, {formData.defaultLocation.lng.toFixed(4)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end mt-8 pt-6 border-t border-gray-100">
            <button 
                type="submit" 
                className="bg-[#1d5cab] hover:bg-[#2372bd] text-white font-bold py-3 px-8 rounded-lg transition shadow-md hover:shadow-lg transform active:scale-95"
            >
                {t('profile.update-profile')}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default ProfilePage;
